const YT_ID_RE = /^[A-Za-z0-9_-]{11}$/;

export function parseYoutubeId(raw) {
  const s = String(raw || '').trim();
  if (!s) return null;
  if (YT_ID_RE.test(s)) return s;
  const m = s.match(
    /(?:youtu\.be\/|youtube\.com\/(?:watch\?(?:[^#]*&)?v=|embed\/|shorts\/|v\/)|youtube\.com\/live\/)([A-Za-z0-9_-]{11})/
  );
  return m && YT_ID_RE.test(m[1]) ? m[1] : null;
}

export function youtubeBlockHtml(id, widthPct) {
  const safe = String(id || '').replace(/[^A-Za-z0-9_-]/g, '').slice(0, 11);
  if (!YT_ID_RE.test(safe)) return '';
  const wRaw = String(widthPct || '').trim();
  const w = /^[0-9]+(?:\.[0-9]+)?%$/.test(wRaw) ? wRaw : '';
  const style = w ? ` style="width: ${w};"` : '';
  return (
    `<div class="cmc-notice-yt" data-yt="${safe}" contenteditable="false"${style}>` +
    `<iframe src="https://www.youtube.com/embed/${safe}" title="YouTube" ` +
    `allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" ` +
    `allowfullscreen></iframe></div>`
  );
}

export function hydrateNoticeHtml(html) {
  return String(html || '').replace(
    /<div\b([^>]*data-yt\s*=\s*["']([^"']+)["'][^>]*)>([\s\S]*?)<\/div>/gi,
    (_, attrs, raw) => {
      const id = parseYoutubeId(raw) || raw;
      const w = (String(attrs || '').match(/width\s*:\s*([0-9.]+%)/i) || [])[1] || '';
      return youtubeBlockHtml(id, w) || '';
    }
  );
}

export function serializeNoticeHtml(html) {
  const root = document.createElement('div');
  root.innerHTML = String(html || '');
  root.querySelectorAll('[data-img-uploading]').forEach((el) => el.remove());
  root.querySelectorAll('[data-yt]').forEach((el) => {
    const id = parseYoutubeId(el.getAttribute('data-yt'));
    if (!id) {
      el.remove();
      return;
    }
    const stub = document.createElement('div');
    stub.setAttribute('data-yt', id);
    const w = (el.getAttribute('style') || '').match(/width\s*:\s*([0-9.]+%)/i);
    if (w) stub.setAttribute('style', `width: ${w[1]};`);
    el.replaceWith(stub);
  });
  return root.innerHTML;
}

export function escapeHtmlText(s) {
  return String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Remove tabulações do conteúdo colado (paridade com Manual — texto limpo no editor). */
export function stripPasteTabs(value) {
  return String(value || '').replace(/\t/g, '');
}

export function normalizePasteHtml(html) {
  const root = document.createElement('div');
  root.innerHTML = stripPasteTabs(String(html || ''));
  root
    .querySelectorAll('script,style,meta,link,iframe,object,embed,form,input,button,svg,noscript')
    .forEach((n) => n.remove());

  const BLOCK = new Set(['p', 'div', 'h1', 'h2', 'h3', 'h4', 'ul', 'ol', 'li', 'blockquote', 'pre', 'hr', 'br']);
  const INLINE = new Set(['b', 'strong', 'i', 'em', 'u', 's', 'strike', 'code', 'br']);
  const HEADING = { h3: 'h2', h4: 'h2', h5: 'h2', h6: 'h2' };

  const walk = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      return document.createTextNode(stripPasteTabs(node.textContent || ''));
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return null;
    let tag = String(node.tagName || '').toLowerCase();
    if (HEADING[tag]) tag = HEADING[tag];
    if (tag === 'img') {
      const guid = node.getAttribute('data-img');
      if (!guid) return null;
      const img = document.createElement('img');
      img.setAttribute('data-img', String(guid).replace(/[^a-zA-Z0-9-]/g, '').slice(0, 36));
      const w = (node.getAttribute('style') || '').match(/width\s*:\s*([0-9.]+%)/i);
      img.setAttribute('style', w ? `width: ${w[1]};` : 'width: 100%;');
      return img;
    }
    if (tag === 'div' && node.getAttribute('data-yt')) {
      const id = parseYoutubeId(node.getAttribute('data-yt'));
      if (!id) return null;
      const w = (node.getAttribute('style') || '').match(/width\s*:\s*([0-9.]+%)/i);
      const wrap = document.createElement('div');
      wrap.innerHTML = youtubeBlockHtml(id, w ? w[1] : '');
      return wrap.firstChild;
    }
    if (tag === 'a') {
      const frag = document.createDocumentFragment();
      Array.from(node.childNodes).forEach((c) => {
        const n = walk(c);
        if (n) frag.appendChild(n);
      });
      return frag;
    }
    if (tag === 'span' || tag === 'font' || tag === 'label' || tag === 'o:p') {
      const style = String(node.getAttribute?.('style') || '').toLowerCase();
      const fw = ((style.match(/font-weight\s*:\s*([^;]+)/) || [])[1] || '').trim();
      const fs = ((style.match(/font-style\s*:\s*([^;]+)/) || [])[1] || '').trim();
      const td = ((style.match(/text-decoration(?:-line)?\s*:\s*([^;]+)/) || [])[1] || '').trim();
      const wraps = [];
      if (/bold|[6-9]00/.test(fw)) wraps.push('b');
      if (/italic/.test(fs)) wraps.push('i');
      if (/underline/.test(td)) wraps.push('u');
      if (/line-through/.test(td)) wraps.push('s');
      let inner = document.createDocumentFragment();
      Array.from(node.childNodes).forEach((c) => {
        const n = walk(c);
        if (n) inner.appendChild(n);
      });
      if (!wraps.length) return inner;
      let cur = inner;
      wraps.reverse().forEach((t) => {
        const el = document.createElement(t);
        el.appendChild(cur);
        cur = el;
      });
      return cur;
    }
    if (BLOCK.has(tag) || INLINE.has(tag)) {
      const outTag = tag === 'strong' ? 'b' : tag === 'em' ? 'i' : tag === 'strike' ? 's' : tag;
      if (outTag === 'pre' || outTag === 'code') {
        const el = document.createElement(outTag);
        el.textContent = stripPasteTabs(node.textContent || '');
        return el;
      }
      const el = document.createElement(outTag === 'div' ? 'div' : outTag);
      Array.from(node.childNodes).forEach((c) => {
        const n = walk(c);
        if (n) el.appendChild(n);
      });
      return el;
    }
    const frag = document.createDocumentFragment();
    Array.from(node.childNodes).forEach((c) => {
      const n = walk(c);
      if (n) frag.appendChild(n);
    });
    return frag;
  };

  const out = document.createElement('div');
  Array.from(root.childNodes).forEach((c) => {
    const n = walk(c);
    if (n) out.appendChild(n);
  });
  Array.from(out.childNodes).forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE && String(node.textContent || '').trim()) {
      const div = document.createElement('div');
      div.textContent = stripPasteTabs(node.textContent);
      node.replaceWith(div);
    }
  });
  return stripPasteTabs(
    out.innerHTML
      .replace(/\s(style|class|id|dir|lang|face|size|color|bgcolor|align|width|height)\s*=\s*(["'])[\s\S]*?\2/gi, '')
      .replace(/\s(style|class|id|dir|lang|face|size|color|bgcolor|align|width|height)\s*=\s*[^\s>]+/gi, '')
  );
}

export function normalizePastePlainText(text) {
  const lines = stripPasteTabs(String(text || ''))
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n');
  if (lines.length === 1) return escapeHtmlText(lines[0]);
  return lines.map((line) => `<div>${escapeHtmlText(line) || '<br>'}</div>`).join('');
}

export function toLocalInputValue(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Converte `YYYY-MM-DDTHH:mm` do picker (relógio local do browser) para ISO UTC. */
export function fromLocalInputValue(value) {
  if (!value) return null;
  const m = String(value)
    .trim()
    .match(/^(\d{4})-(\d{2})-(\d{2})T(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (m) {
    const d = new Date(
      Number(m[1]),
      Number(m[2]) - 1,
      Number(m[3]),
      Number(m[4]),
      Number(m[5]),
      m[6] != null ? Number(m[6]) : 0,
      0
    );
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** True se a data local de exibição é futura (permite Agendar; senão Publicar agora). */
export function isFutureLocalPublishAt(value, now = new Date()) {
  if (!value) return false;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return false;
  return d.getTime() > now.getTime() + 30 * 1000;
}
